import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../services/api';

interface ApiKey {
  id: number;
  key_name: string;
  api_key: string;
  full_key?: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ApiDocs: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [showKeys, setShowKeys] = useState<{ [key: number]: boolean }>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const queryClient = useQueryClient();

  const { data: keysData, isLoading } = useQuery(
    'apiKeys',
    async () => {
      const response = await apiClient.get('/keys');
      return response.data.keys;
    }
  );

  const createKeyMutation = useMutation(
    async (name: string) => {
      const response = await apiClient.post('/keys', { key_name: name });
      return response.data.key;
    },
    {
      onSuccess: (newKey) => {
        queryClient.invalidateQueries('apiKeys');
        setCreateDialogOpen(false);
        setKeyName('');
        // Mostrar a chave completa temporariamente
        setShowKeys({ ...showKeys, [newKey.id]: true });
        setSnackbar({ open: true, message: 'API Key criada com sucesso! Copie agora, ela não será mostrada novamente.' });
      },
    }
  );

  const deleteKeyMutation = useMutation(
    async (id: number) => {
      await apiClient.delete(`/keys/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('apiKeys');
        setSnackbar({ open: true, message: 'API Key deletada com sucesso' });
      },
    }
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copiado para a área de transferência!' });
  };

  const baseUrl = window.location.origin.replace(':3000', ':3001');

  const apiExamples = [
    {
      title: 'Listar Bots',
      method: 'GET',
      endpoint: '/api/v1/bots',
      description: 'Retorna todos os bots do usuário',
      curl: `curl -X GET "${baseUrl}/api/v1/bots" \\
  -H "Authorization: YOUR_API_KEY"`,
      response: `{
  "success": true,
  "data": [
    {
      "id": 1,
      "name": "Bot Vendas",
      "phone_number": "5511999999999",
      "is_active": true,
      "created_at": "2024-01-01T00:00:00.000Z",
      "whitelist": ["5511888888888", "5511777777777"]
    }
  ]
}`
    },
    {
      title: 'Detalhes do Bot',
      method: 'GET',
      endpoint: '/api/v1/bots/:id',
      description: 'Retorna detalhes de um bot específico',
      curl: `curl -X GET "${baseUrl}/api/v1/bots/1" \\
  -H "Authorization: YOUR_API_KEY"`,
      response: `{
  "success": true,
  "data": {
    "id": 1,
    "name": "Bot Vendas",
    "phone_number": "5511999999999",
    "is_active": true,
    "whitelist": ["5511888888888"],
    "whitelist_message": "Desculpe, você não tem permissão...",
    "stop_message": "PARAR BOT",
    "resume_message": "CONTINUAR BOT",
    "created_at": "2024-01-01T00:00:00.000Z"
  }
}`
    },
    {
      title: 'Ativar/Desativar Bot',
      method: 'PATCH',
      endpoint: '/api/v1/bots/:id/status',
      description: 'Altera o status de ativação do bot',
      curl: `curl -X PATCH "${baseUrl}/api/v1/bots/1/status" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"is_active": false}'`,
      response: `{
  "success": true,
  "data": {
    "id": 1,
    "name": "Bot Vendas",
    "is_active": false
  }
}`
    },
    {
      title: 'Atualizar Whitelist (Completa)',
      method: 'PATCH',
      endpoint: '/api/v1/bots/:id/whitelist',
      description: 'Substitui toda a whitelist de contatos autorizados',
      curl: `curl -X PATCH "${baseUrl}/api/v1/bots/1/whitelist" \\
  -H "Authorization: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "whitelist": ["5511999999999", "5511888888888"],
    "whitelist_message": "Contato não autorizado"
  }'`,
      response: `{
  "success": true,
  "data": {
    "id": 1,
    "name": "Bot Vendas",
    "whitelist": ["5511999999999", "5511888888888"],
    "whitelist_message": "Contato não autorizado"
  }
}`
    },
    {
      title: 'Adicionar Número à Whitelist',
      method: 'POST',
      endpoint: '/api/v1/bots/:id/whitelist/add',
      description: 'Adiciona um único número à whitelist existente',
      curl: `curl -X POST "${baseUrl}/api/v1/bots/1/whitelist/add" \\
  -H "Authorization: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "number": "5511999999999"
  }'`,
      response: `{
  "success": true,
  "message": "Número adicionado à whitelist",
  "data": {
    "id": 1,
    "name": "Bot Vendas",
    "whitelist": ["5511888888888", "5511999999999"]
  }
}`
    },
    {
      title: 'Remover Número da Whitelist',
      method: 'POST',
      endpoint: '/api/v1/bots/:id/whitelist/remove',
      description: 'Remove um único número da whitelist',
      curl: `curl -X POST "${baseUrl}/api/v1/bots/1/whitelist/remove" \\
  -H "Authorization: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "number": "5511999999999"
  }'`,
      response: `{
  "success": true,
  "message": "Número removido da whitelist",
  "data": {
    "id": 1,
    "name": "Bot Vendas",
    "whitelist": ["5511888888888"]
  }
}`
    },
  ];

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          API Documentation
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Integre outros sistemas com nossa plataforma usando a API REST.
        </Typography>

        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="API Keys" />
          <Tab label="Documentação" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Suas API Keys</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Criar Nova Key
            </Button>
          </Box>

          {isLoading ? (
            <Typography>Carregando...</Typography>
          ) : keysData?.length === 0 ? (
            <Alert severity="info">
              Você ainda não tem nenhuma API Key. Crie uma para começar a usar a API.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>API Key</TableCell>
                    <TableCell>Criada em</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keysData?.map((key: ApiKey) => (
                    <TableRow key={key.id}>
                      <TableCell>{key.key_name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {showKeys[key.id] && key.full_key ? key.full_key : key.api_key}
                          </Typography>
                          {key.full_key && (
                            <IconButton
                              size="small"
                              onClick={() => setShowKeys({ ...showKeys, [key.id]: !showKeys[key.id] })}
                            >
                              {showKeys[key.id] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(key.full_key || key.api_key)}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(key.created_at).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (window.confirm('Tem certeza que deseja deletar esta API Key?')) {
                              deleteKeyMutation.mutate(key.id);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Como usar a API
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>URL Base:</strong> {baseUrl}/api/v1
            </Typography>
            <Typography variant="subtitle2">
              <strong>Autenticação:</strong> Todas as requisições devem incluir o header Authorization com sua API Key diretamente (sem prefixo Bearer)
            </Typography>
          </Alert>

          <Typography variant="body2" paragraph>
            Exemplo de header de autenticação:
          </Typography>
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              Authorization: sk_your_api_key_here
            </Typography>
          </Paper>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Endpoints Disponíveis
          </Typography>

          {apiExamples.map((example, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip 
                    label={example.method} 
                    size="small" 
                    color={example.method === 'GET' ? 'primary' : 'warning'}
                  />
                  <Typography>{example.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {example.endpoint}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  {example.description}
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>
                  Exemplo cURL:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.900' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ fontFamily: 'monospace', color: 'white', whiteSpace: 'pre-wrap' }}
                    >
                      {example.curl}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(example.curl)}
                      sx={{ color: 'white' }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Box>
                </Paper>

                <Typography variant="subtitle2" gutterBottom>
                  Resposta de Exemplo:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                    >
                      {example.response}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(example.response)}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Box>
                </Paper>
              </AccordionDetails>
            </Accordion>
          ))}

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="subtitle2">
              <strong>Importante:</strong> Mantenha suas API Keys seguras. Não as compartilhe publicamente ou as inclua em código-fonte versionado.
            </Typography>
          </Alert>
        </TabPanel>
      </Paper>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Criar Nova API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da Key"
            fullWidth
            variant="outlined"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="Ex: Integração ERP"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={() => createKeyMutation.mutate(keyName)} 
            variant="contained"
            disabled={!keyName.trim()}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
};

export default ApiDocs;